import { Context, Data, Effect, Schema } from "effect";
import { InvoiceSchema } from "../entities/Invoice";

/**
 * Domain errors for PDF rendering operations
 */
export class PDFGenerationError extends Data.TaggedError("@/PDFGenerationError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly invoiceId?: string;
  readonly invoiceNumber?: string;
}> {}

export class PDFComplianceError extends Data.TaggedError("@/PDFComplianceError")<{
  readonly cause?: unknown;
  readonly message: string;
  readonly complianceIssue: string;
}> {}

/**
 * German legal information required for invoice PDFs
 */
export const GermanLegalInfoSchema = Schema.Struct({
  // Contact information
  companyName: Schema.String.pipe(Schema.nonEmptyString()),
  companyAddress: Schema.Struct({
    street: Schema.String.pipe(Schema.nonEmptyString()),
    zip: Schema.String.pipe(Schema.nonEmptyString()),
    city: Schema.String.pipe(Schema.nonEmptyString()),
    country: Schema.String.pipe(Schema.nonEmptyString()),
  }),
  phone: Schema.String.pipe(Schema.nonEmptyString()),
  email: Schema.String,
  website: Schema.optional(Schema.String),

  // Legal information
  managingDirector: Schema.String.pipe(Schema.nonEmptyString()),
  commercialRegister: Schema.String.pipe(Schema.nonEmptyString()),
  commercialRegisterNumber: Schema.String.pipe(Schema.nonEmptyString()),
  jurisdiction: Schema.String.pipe(Schema.nonEmptyString()),

  // Tax information
  vatId: Schema.String.pipe(Schema.nonEmptyString(), Schema.pattern(/^DE\d{9}$/)),
  taxNumber: Schema.String.pipe(Schema.nonEmptyString()),

  // Bank information
  bankDetails: Schema.Struct({
    bankName: Schema.String.pipe(Schema.nonEmptyString()),
    iban: Schema.String.pipe(Schema.nonEmptyString(), Schema.pattern(/^DE\d{20}$/)),
    bic: Schema.String.pipe(
      Schema.nonEmptyString(),
      Schema.pattern(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/)
    ),
  }),
});

export type GermanLegalInfo = Schema.Schema.Type<typeof GermanLegalInfoSchema>;

/**
 * PDF rendering options for compliance and formatting
 */
export const PDFRenderOptionsSchema = Schema.Struct({
  // PDF/A-3 compliance settings
  pdfACompliance: Schema.Literal("PDF/A-3"),
  embedFonts: Schema.Boolean,
  colorProfile: Schema.String,

  // German legal requirements
  legalInfo: GermanLegalInfoSchema,

  // ZUGfERD/Factur-X preparation
  includeXMLAttachment: Schema.Boolean,
  xmlStandard: Schema.Union(Schema.Literal("ZUGfERD"), Schema.Literal("Factur-X")),

  // Layout and formatting
  language: Schema.Literal("de"),
  currency: Schema.Literal("EUR"),
  dateFormat: Schema.Literal("DD.MM.YYYY"),

  // Optional customization
  logoBytes: Schema.optional(Schema.Uint8Array),
  customFooter: Schema.optional(Schema.String),
});

export type PDFRenderOptions = Schema.Schema.Type<typeof PDFRenderOptionsSchema>;

/**
 * Data structure containing all information needed for PDF rendering
 */
export const InvoicePDFDataSchema = Schema.Struct({
  invoice: InvoiceSchema,
  renderOptions: PDFRenderOptionsSchema,
});

export type InvoicePDFData = Schema.Schema.Type<typeof InvoicePDFDataSchema>;

/**
 * Service interface for rendering invoices to PDF/A-3 compliant documents
 * with German legal requirements
 */
export interface PDFRendererSchema {
  /**
   * Render an invoice to a PDF/A-3 compliant document with German legal information
   *
   * @param invoicePDFData - Complete invoice data and rendering options
   * @returns Effect containing PDF bytes or rendering errors
   */
  render: (
    invoicePDFData: InvoicePDFData
  ) => Effect.Effect<Uint8Array, PDFGenerationError | PDFComplianceError>;

  /**
   * Validate that the provided legal information meets German requirements
   *
   * @param legalInfo - German legal information to validate
   * @returns Effect that succeeds if valid or fails with compliance error
   */
  validateGermanLegalInfo: (legalInfo: GermanLegalInfo) => Effect.Effect<void, PDFComplianceError>;

  /**
   * Check if generated PDF meets PDF/A-3 compliance standards
   *
   * @param pdfBytes - PDF document bytes to validate
   * @returns Effect that succeeds if compliant or fails with compliance error
   */
  validatePDFACompliance: (pdfBytes: Uint8Array) => Effect.Effect<void, PDFComplianceError>;

  /**
   * Get default German legal information template
   * (useful for testing and initial setup)
   *
   * @returns Default legal information structure
   */
  getDefaultGermanLegalInfo: () => GermanLegalInfo;

  /**
   * Get default PDF rendering options
   *
   * @param legalInfo - German legal information to include
   * @returns Default rendering options with provided legal info
   */
  getDefaultRenderOptions: (legalInfo: GermanLegalInfo) => PDFRenderOptions;
}

/**
 * Tag for dependency injection
 */
export class PDFRenderer extends Context.Tag("@/domain/services/PDFRenderer")<
  PDFRenderer,
  PDFRendererSchema
>() {}
